import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

// Helper to create a portal at document.body level
const Portal = ({ children }) => {
  const portalNode = useRef(null);
  
  if (!portalNode.current) {
    portalNode.current = document.createElement('div');
    portalNode.current.style.position = 'fixed';
    portalNode.current.style.zIndex = '10000';
    portalNode.current.style.top = '0';
    portalNode.current.style.left = '0';
    portalNode.current.style.width = '100%';
    portalNode.current.style.height = '100%';
    portalNode.current.style.pointerEvents = 'none';
  }
  
  useEffect(() => {
    document.body.appendChild(portalNode.current);
    return () => {
      document.body.removeChild(portalNode.current);
    };
  }, []);
  
  return ReactDOM.createPortal(children, portalNode.current);
};

const FineTuningExamples = ({ 
  nodeId, 
  examples = [], 
  onUpdate,
  isOpen = false,
  onClose
}) => {
  const [currentExamples, setCurrentExamples] = useState(examples);
  const [currentEditIndex, setCurrentEditIndex] = useState(null);
  const [newExampleTitle, setNewExampleTitle] = useState('');
  const [newExampleMessages, setNewExampleMessages] = useState([
    { role: 'user', content: '' },
    { role: 'assistant', content: '' }
  ]);
  const [isAnimating, setIsAnimating] = useState(false);
  const drawerRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      // Prevent body scrolling when drawer is open
      document.body.style.overflow = 'hidden';
      
      // Focus trap inside drawer for accessibility
      setTimeout(() => {
        if (drawerRef.current) {
          drawerRef.current.focus();
        }
      }, 100);
    } else {
      setIsAnimating(false);
      document.body.style.overflow = '';
    }
    
    // Handle escape key press
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    
    window.addEventListener('keydown', handleEscKey);
    
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    // Delay actual closing to allow animation to complete
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleAddExample = () => {
    if (!newExampleTitle.trim()) {
      alert('Example title is required');
      return;
    }

    // Validate that all messages have content
    if (newExampleMessages.some(msg => !msg.content.trim())) {
      alert('All messages must have content');
      return;
    }

    const newExample = {
      id: `example-${Date.now()}`,
      title: newExampleTitle,
      description: `Example conversation for "${newExampleTitle}"`,
      messages: [...newExampleMessages]
    };

    const updatedExamples = [...currentExamples, newExample];
    setCurrentExamples(updatedExamples);
    onUpdate(nodeId, updatedExamples);

    // Reset form for next example
    setNewExampleTitle('');
    setNewExampleMessages([
      { role: 'user', content: '' },
      { role: 'assistant', content: '' }
    ]);
  };

  const handleEditExample = (index) => {
    setCurrentEditIndex(index);
    setNewExampleTitle(currentExamples[index].title);
    setNewExampleMessages([...currentExamples[index].messages]);
  };

  const handleUpdateExample = () => {
    if (!newExampleTitle.trim()) {
      alert('Example title is required');
      return;
    }

    // Validate that all messages have content
    if (newExampleMessages.some(msg => !msg.content.trim())) {
      alert('All messages must have content');
      return;
    }

    const updatedExamples = [...currentExamples];
    updatedExamples[currentEditIndex] = {
      ...updatedExamples[currentEditIndex],
      title: newExampleTitle,
      description: `Example conversation for "${newExampleTitle}"`,
      messages: [...newExampleMessages]
    };

    setCurrentExamples(updatedExamples);
    onUpdate(nodeId, updatedExamples);

    // Reset edit form
    setCurrentEditIndex(null);
    setNewExampleTitle('');
    setNewExampleMessages([
      { role: 'user', content: '' },
      { role: 'assistant', content: '' }
    ]);
  };

  const handleDeleteExample = (index) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this example?');
    if (confirmDelete) {
      const updatedExamples = currentExamples.filter((_, i) => i !== index);
      setCurrentExamples(updatedExamples);
      onUpdate(nodeId, updatedExamples);
    }
  };

  const addMessage = () => {
    setNewExampleMessages([...newExampleMessages, { role: newExampleMessages.length % 2 === 0 ? 'user' : 'assistant', content: '' }]);
  };

  const updateMessageContent = (index, content) => {
    const updatedMessages = [...newExampleMessages];
    updatedMessages[index].content = content;
    setNewExampleMessages(updatedMessages);
  };

  const removeMessage = (index) => {
    const updatedMessages = newExampleMessages.filter((_, i) => i !== index);
    setNewExampleMessages(updatedMessages);
  };
  
  if (!isOpen) return null;

  // The drawer content to render inside the portal
  const drawer = (
    <>
      {/* Backdrop overlay */}
      <div 
        className="examples-drawer-backdrop"
        style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100%', 
          height: '100%', 
          background: 'rgba(15, 23, 42, 0.5)', 
          backdropFilter: 'blur(2px)',
          zIndex: 9998,
          transition: 'opacity 0.25s ease-in-out',
          opacity: isAnimating ? 1 : 0,
          pointerEvents: 'auto'
        }}
        onClick={handleClose}
        aria-hidden="true"
      />
      
      {/* Drawer panel */}
      <div 
        ref={drawerRef}
        className="examples-drawer-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
        tabIndex="-1"
        style={{ 
          position: 'fixed',
          top: 0,
          right: 0,
          width: '750px', 
          height: '100vh',
          background: '#ffffff', 
          boxShadow: '-10px 0 30px rgba(0, 0, 0, 0.12)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          transform: isAnimating ? 'translateX(0)' : 'translateX(100%)',
          pointerEvents: 'auto'
        }}
      >
        {/* Drawer header */}
        <div 
          className="examples-drawer-header"
          style={{ 
            padding: '16px 24px',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            flexShrink: 0
          }}
        >
          <h2 
            id="drawer-title"
            style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#0f172a', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '12px',
              margin: 0
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 6V12L16 14M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Fine-tuning Examples</span>
          </h2>
          <button 
            onClick={handleClose} 
            aria-label="Close examples panel"
            style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              borderRadius: '6px',
              background: 'transparent',
              border: 'none',
              color: '#64748b',
              cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => {e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#0f172a';}}
            onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b';}}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        {/* Drawer content */}
        <div 
          className="examples-drawer-content"
          style={{ 
            flex: '1 1 auto',
            overflowY: 'auto',
            padding: '0 24px',
          }}
        >
          {/* Existing examples list */}
          <div style={{ padding: '24px 0', borderBottom: '1px solid #f1f5f9' }}>
            <h3 style={{ 
              fontSize: '15px', 
              fontWeight: '600', 
              color: '#0f172a', 
              marginTop: 0,
              marginBottom: '16px' 
            }}>
              Existing Examples
            </h3>
            
            {currentExamples.length === 0 ? (
              <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '8px', textAlign: 'center' }}>
                <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>No examples added yet. Add your first example below.</p>
              </div>
            ) : (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                gap: '16px',
                marginBottom: '16px'
              }}>
                {currentExamples.map((example, index) => (
                  <div key={example.id} style={{ 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '8px', 
                    padding: '16px',
                    position: 'relative',
                    background: '#fff'
                  }}>
                    <div style={{ 
                      marginBottom: '12px', 
                      fontSize: '14px', 
                      fontWeight: '600',
                      color: '#0f172a',
                      paddingRight: '24px'
                    }}>
                      {example.title}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#64748b', 
                      marginBottom: '14px'
                    }}>
                      {example.messages.length} message{example.messages.length !== 1 ? 's' : ''}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        onClick={() => handleEditExample(index)}
                        style={{ 
                          padding: '6px 10px', 
                          background: '#eff6ff', 
                          color: '#3b82f6', 
                          border: '1px solid #bfdbfe', 
                          borderRadius: '4px', 
                          cursor: 'pointer', 
                          fontSize: '12px',
                          flexGrow: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px'
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteExample(index)}
                        style={{ 
                          padding: '6px 10px', 
                          background: '#fff1f0', 
                          color: '#ef4444', 
                          border: '1px solid #fecaca', 
                          borderRadius: '4px', 
                          cursor: 'pointer', 
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px'
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 6h18" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Add/edit example form */}
          <div style={{ padding: '24px 0' }}>
            <h3 style={{ 
              fontSize: '15px', 
              fontWeight: '600', 
              color: '#0f172a', 
              marginTop: 0,
              marginBottom: '16px' 
            }}>
              {currentEditIndex !== null ? 'Edit Example' : 'Add New Example'}
            </h3>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '500', 
                color: '#0f172a', 
                marginBottom: '8px' 
              }}>
                Example Title
              </label>
              <input 
                type="text" 
                value={newExampleTitle} 
                onChange={(e) => setNewExampleTitle(e.target.value)}
                placeholder="E.g., Greeting conversation"
                style={{ 
                  width: '100%', 
                  padding: '10px 12px', 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '6px', 
                  fontSize: '14px',
                  marginBottom: '8px'
                }}
              />
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                Give your example a descriptive title
              </div>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '12px' 
              }}>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: '#0f172a'
                }}>
                  Example Messages
                </label>
                <button 
                  onClick={addMessage}
                  style={{ 
                    padding: '6px 10px', 
                    background: '#f0fdf4', 
                    color: '#16a34a', 
                    border: '1px solid #bbf7d0', 
                    borderRadius: '6px', 
                    cursor: 'pointer', 
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5v14M5 12h14" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Add Message
                </button>
              </div>
              
              {newExampleMessages.map((message, index) => (
                <div 
                  key={index} 
                  style={{ 
                    marginBottom: '12px',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <span style={{ 
                      fontSize: '12px', 
                      fontWeight: '500',
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      {message.role === 'user' ? (
                        <>
                          <span style={{ 
                            display: 'inline-block', 
                            width: '16px', 
                            height: '16px', 
                            borderRadius: '50%', 
                            background: '#f0f9ff', 
                            border: '1px solid #bae6fd',
                            textAlign: 'center',
                            lineHeight: '14px',
                            fontSize: '10px',
                            color: '#0ea5e9',
                            fontWeight: 'bold'
                          }}>U</span>
                          User
                        </>
                      ) : (
                        <>
                          <span style={{ 
                            display: 'inline-block', 
                            width: '16px', 
                            height: '16px', 
                            borderRadius: '50%', 
                            background: '#f9f5ff', 
                            border: '1px solid #e9d5ff',
                            textAlign: 'center',
                            lineHeight: '14px',
                            fontSize: '10px',
                            color: '#8b5cf6',
                            fontWeight: 'bold'
                          }}>A</span>
                          Assistant
                        </>
                      )}
                    </span>
                    {newExampleMessages.length > 2 && (
                      <button 
                        onClick={() => removeMessage(index)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: '#94a3b8',
                          padding: '4px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px'
                        }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M19 6L5 20M5 6l14 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    )}
                  </div>
                  <textarea
                    value={message.content}
                    onChange={(e) => updateMessageContent(index, e.target.value)}
                    placeholder={`${message.role === 'user' ? 'User' : 'Assistant'} message...`}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '14px',
                      minHeight: '80px',
                      resize: 'vertical',
                      background: message.role === 'user' ? '#f8fafc' : '#fff'
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Drawer footer */}
        <div 
          className="examples-drawer-footer"
          style={{ 
            padding: '16px 24px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0,
            background: '#fff',
            gap: '12px'
          }}
        >
          {currentEditIndex !== null && (
            <button 
              onClick={() => {
                setCurrentEditIndex(null);
                setNewExampleTitle('');
                setNewExampleMessages([
                  { role: 'user', content: '' },
                  { role: 'assistant', content: '' }
                ]);
              }}
              style={{ 
                padding: '10px 20px', 
                background: '#f1f5f9', 
                color: '#475569', 
                border: 'none', 
                borderRadius: '8px', 
                cursor: 'pointer', 
                fontSize: '14px',
                fontWeight: '500',
              }}
            >
              Cancel
            </button>
          )}
          <button 
            onClick={currentEditIndex !== null ? handleUpdateExample : handleAddExample}
            style={{ 
              padding: '10px 20px', 
              background: '#6941c6', 
              color: '#fff', 
              border: 'none', 
              borderRadius: '8px', 
              cursor: 'pointer', 
              fontSize: '14px',
              fontWeight: '500',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => {e.currentTarget.style.background = '#5f35b5';}}
            onMouseLeave={(e) => {e.currentTarget.style.background = '#6941c6';}}
          >
            {currentEditIndex !== null ? 'Update Example' : 'Add Example'}
          </button>
        </div>
      </div>
    </>
  );
  
  // Render the drawer inside a React Portal
  return <Portal>{drawer}</Portal>;
};

export default FineTuningExamples;